import React, { useEffect, useMemo, useState } from 'react';
import { SECTION_NOTE_MAX_LENGTH, normalizeSectionNote } from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import SectionEditorModalFrame from './SectionEditorModalFrame';

// Room for three or four lines before the box scrolls
const NOTE_INPUT_MIN_HEIGHT = 120;

const createStyles = (theme: Theme) => ({
  body: {
    padding: theme.spacing.xl,
  },
  label: {
    display: 'block',
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.sm,
  },
  textarea: {
    display: 'block',
    width: '100%',
    minHeight: NOTE_INPUT_MIN_HEIGHT,
    resize: 'vertical' as const,
    padding: theme.spacing.sm,
    borderRadius: theme.radii.md,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    lineHeight: theme.typography.lineHeights.normal,
    outline: 'none',
  },
  counter: {
    marginTop: theme.spacing.xs,
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  },
});

export interface SectionNoteEditorModalProps {
  isOpen: boolean;
  sectionTitle: string;
  initialValue: string | null;
  onClose: () => void;
  /** null clears the note. */
  onApply: (note: string | null) => void;
}

const SectionNoteEditorModal: React.FC<SectionNoteEditorModalProps> = ({
  isOpen,
  sectionTitle,
  initialValue,
  onClose,
  onApply,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [text, setText] = useState(initialValue ?? '');

  useEffect(() => {
    if (isOpen) {
      setText(initialValue ?? '');
    }
  }, [isOpen, initialValue]);

  return (
    <SectionEditorModalFrame
      isOpen={isOpen}
      title="Section note"
      subtitle={sectionTitle.trim() || undefined}
      onClose={onClose}
      onApply={() => onApply(normalizeSectionNote(text))}
      onClear={initialValue != null ? () => onApply(null) : undefined}
    >
      <div style={styles.body}>
        <label htmlFor="section-note-input" style={styles.label}>
          Note
        </label>
        <textarea
          id="section-note-input"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="e.g. Drums in on the pickup, build into the chorus"
          maxLength={SECTION_NOTE_MAX_LENGTH}
          autoFocus
          style={styles.textarea}
        />
        <div style={styles.counter}>
          {text.length} / {SECTION_NOTE_MAX_LENGTH}
        </div>
      </div>
    </SectionEditorModalFrame>
  );
};

export default SectionNoteEditorModal;
